import { createElement, useContext } from "react";

import { MenuContext } from "./Menu";
import { Icon } from "../icon/Icon";
import { Link } from "../link/Link";

import type {
  ReactNode,
  SyntheticEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import type { IconProps } from "../icon/Icon";

export interface MenuItemProps {
  children: ReactNode;
  disabled?: boolean;
  href?: string;
  icon?: IconProps["icon"];
  iconVariant?: IconProps["variant"];
  id?: string;
  index?: number;
  target?: string;
  value?: string;
  onClick?: (event: SyntheticEvent) => void;
  onFocus?: () => void;
  onMouseLeave?: () => void;
  onMouseMove?: () => void;
}

export const MenuItem = ({
  children,
  disabled,
  href,
  icon,
  iconVariant,
  id,
  index = 0,
  target,
  value,
  onClick,
}: MenuItemProps) => {
  const {
    activeIndex,
    dataRef,
    listRef,
    getItemProps,
    onChange,
    setActiveIndex,
    setSelectedIndex,
  } = useContext(MenuContext);

  function handleSelect(event: SyntheticEvent) {
    setSelectedIndex(index);
    onChange({ value: value ?? null });
    setActiveIndex(null);
    onClick?.(event);
  }

  function handleKeyDown(event: ReactKeyboardEvent) {
    if (
      event.key === "Enter" ||
      (event.key === " " && !dataRef.current.typing)
    ) {
      handleSelect(event);
    }
  }

  const baseProps = {
    ...getItemProps({
      onClick: handleSelect,
      onKeyDown: handleKeyDown,
      disabled,
    }),
    className: "MenuItem",
    role: "menuitem",
    ref: (node: HTMLButtonElement | HTMLAnchorElement | HTMLDivElement) =>
      (listRef.current[index] = node),
    "aria-disabled": disabled,
    "aria-current": activeIndex === index,
    "data-id": id,
  };

  const renderedChildren = (
    <>
      {icon && <Icon icon={icon} variant={iconVariant} size="sm" />}
      {children}
    </>
  );

  if (href) {
    return createElement(
      Link,
      {
        ...baseProps,
        to: href ?? "/",
        target,
        ref: (node: HTMLAnchorElement) => (listRef.current[index] = node),
      },
      renderedChildren,
    );
  }

  if (onClick) {
    return createElement(
      "button",
      {
        ...baseProps,
        ref: (node: HTMLButtonElement) => (listRef.current[index] = node),
      },
      renderedChildren,
    );
  }

  return createElement(
    "div",
    {
      ...baseProps,
      ref: (node: HTMLDivElement) => (listRef.current[index] = node),
    },
    renderedChildren,
  );
};
